import _extends from "@babel/runtime/helpers/extends";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
var _excluded = ["apiKey", "onBlur", "onFocus", "onKeyDown", "onKeyUp", "onLocationSelect"];
import * as React from 'react';
import { debounce } from 'lodash-es';
import { safeInvoke, loadGoogleApi, keys, ColorPreset, isTruthy } from '../../../../library';
import { IconLocation } from '../../../../icons';
import { Input } from '../../text-input';
import { Popover } from '../../../popover';
import { Pane } from '../../../pane';
import { OptionItem, OptionList, selectClasses } from '../../select';
export var LocationSelect = function LocationSelect(props) {
  var _autoCompleteService = React.useRef();
  var inputRef = React.useRef(null);
  var _optionListRef = React.useRef(null);
  var _React$useState = React.useState(false),
    _React$useState2 = _slicedToArray(_React$useState, 2),
    isOpen = _React$useState2[0],
    setIsOpen = _React$useState2[1];
  var _React$useState3 = React.useState(false),
    _React$useState4 = _slicedToArray(_React$useState3, 2),
    isDismissed = _React$useState4[0],
    setIsDismissed = _React$useState4[1];
  var _React$useState5 = React.useState(false),
    _React$useState6 = _slicedToArray(_React$useState5, 2),
    isFocused = _React$useState6[0],
    setIsFocused = _React$useState6[1];
  var _React$useState7 = React.useState([]),
    _React$useState8 = _slicedToArray(_React$useState7, 2),
    autoCompleteOptions = _React$useState8[0],
    setAutoCompleteOptions = _React$useState8[1];
  React.useEffect(function () {
    loadGoogleApi(props.apiKey)["catch"](function () {
      console.error('Unable to Load Google Api');
    }).then(function () {
      var gApi = window.google;
      _autoCompleteService.current = new gApi.maps.places.AutocompleteService();
    });
  }, []);
  React.useEffect(function () {
    dFetchAutoCompleteResults(props.value + '');
  }, [props.value]);
  function fetchAutoCompleteResults(value) {
    var _autoCompleteService$;
    if (value === '') {
      setAutoCompleteOptions([]);
      setIsOpen(false);
      return;
    }
    (_autoCompleteService$ = _autoCompleteService.current) === null || _autoCompleteService$ === void 0 || _autoCompleteService$.getPlacePredictions({
      input: value,
      types: ['geocode'],
      componentRestrictions: {
        country: ['us']
      }
    }, function (result, status) {
      if (status === google.maps.places.PlacesServiceStatus.OK) {
        setAutoCompleteOptions(result !== null && result !== void 0 ? result : []);
      } else {
        setAutoCompleteOptions([]);
      }
    });
  }
  ;
  var dFetchAutoCompleteResults = debounce(fetchAutoCompleteResults, 100);
  function handlePopoverVisibilityChange(nextOpenState) {
    if (nextOpenState === true && isTruthy(props.value)) {
      setIsOpen(true);
    }
  }
  ;
  function handlePopoverResize(popover) {
    if (!!inputRef.current && !!popover) {
      popover.style.minWidth = inputRef.current.getBoundingClientRect().width + 'px';
    }
  }
  function handleInputFocus(event) {
    setIsFocused(true);
    safeInvoke(props.onFocus, event);
  }
  ;
  function handleInputBlur(event) {
    setIsDismissed(false);
    setIsFocused(false);
    setIsOpen(false);
    safeInvoke(props.onBlur, event);
  }
  function handleInputKeyDown(event) {
    if (event.which === keys.ARROW_DOWN || event.which === keys.ARROW_UP || event.which === keys.ENTER) {
      setIsDismissed(false);
      if (_optionListRef.current) {
        _optionListRef.current.handleKeyDown(event);
      }
    } else if (_optionListRef.current && event.which === keys.ESCAPE) {
      setIsDismissed(true);
    } else {
      if (isFocused) {
        setIsOpen(true);
      }
    }
    safeInvoke(props.onKeyDown, event);
  }
  function handleInputKeyUp(event) {
    if (_optionListRef.current) {
      var _optionListRef$curren;
      (_optionListRef$curren = _optionListRef.current) === null || _optionListRef$curren === void 0 || _optionListRef$curren.handleKeyUp(event);
    }
    safeInvoke(props.onKeyUp, event);
  }
  function handleLocationSelect(option, e) {
    setIsOpen(false);
    safeInvoke(props.onLocationSelect, option, e);
  }
  var renderOption = function renderOption(option, itemProps) {
    return React.createElement(OptionItem, {
      key: itemProps.id,
      onClick: itemProps.handleClick,
      onMouseOver: itemProps.handleMouseEnter,
      id: itemProps.id,
      isActive: itemProps.modifiers.active,
      isDisabled: itemProps.modifiers.disabled,
      isFocused: itemProps.modifiers.focused
    }, React.createElement(Pane, {
      display: "flex",
      flexAlign: "center"
    }, React.createElement(IconLocation, {
      color: itemProps.modifiers.focused ? ColorPreset.WHITE : ColorPreset.GRAY_400,
      marginRight: 4
    }), React.createElement("div", null, React.createElement("div", null, option.structured_formatting.main_text), React.createElement("div", null, React.createElement("small", null, option.structured_formatting.secondary_text)))));
  };
  function renderOptions() {
    if (props.value === '') return null;
    return React.createElement(OptionList, {
      id: "".concat(props.field, "__list"),
      options: autoCompleteOptions,
      renderOption: renderOption,
      onOptionSelect: handleLocationSelect,
      optionIdProp: "place_id",
      ref: _optionListRef
    });
  }
  ;
  var apiKey = props.apiKey,
    onBlur = props.onBlur,
    onFocus = props.onFocus,
    onKeyDown = props.onKeyDown,
    onKeyUp = props.onKeyUp,
    onLocationSelect = props.onLocationSelect,
    inputProps = _objectWithoutProperties(props, _excluded);
  return React.createElement("div", {
    className: "location-select"
  }, React.createElement(Popover, {
    autoFocus: false,
    enforceFocus: false,
    className: selectClasses.OPTIONS,
    isOpen: isOpen && !isDismissed,
    content: renderOptions(),
    position: "bottom",
    isMinimal: true,
    isDisabled: props.isDisabled || props.readOnly,
    onVisibilityChange: handlePopoverVisibilityChange,
    onResize: handlePopoverResize,
    boundary: props.boundary
  }, React.createElement(Input, _extends({}, inputProps, {
    onBlur: handleInputBlur,
    onFocus: handleInputFocus,
    onKeyDown: handleInputKeyDown,
    onKeyUp: handleInputKeyUp,
    ref: inputRef,
    autoComplete: "off"
  }))));
};
LocationSelect.defaultProps = {
  boundary: 'viewport'
};